import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import api from "../../api";
import {readPref, writePref} from "../../ui-prefs";
import {useTranslate} from "../../useTranslate";
import "./onboarding-dock.css";

// Onboarding dock (Zofino master spec, section "dock"): 7 setup steps for workspace owners/admins.
// Progress is computed by the backend (GET /onboarding/progress); this component only shows it and guides.

const REFRESH_MS = 60 * 1000;
const STEP_ROUTES = {
    invite: "/settings?tab=manage-members",
    goal: "/goals",
    projects: "/projects",
    milestones: "/projects",
    tasks: "/tasks",
    assignees: "/tasks",
    teams: "/teams",
};

// pages with a message box at the bottom: the dock (and its launcher) would cover it, so it is hidden there
const HIDDEN_PAGES = [/^\/inbox/];

// per user and workspace, kept when signing out (system/ui-prefs.js): a minimized dock stays minimized at the next sign-in
const prefName = (workspaceId, name) => `onboarding.${name}.${workspaceId}`;

const OnboardingDock = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const isDesktop = useSelector((state) => state.app.isDesktop);
    const workspaceId = useSelector((state) => state.app.workspaceMembershipData?.workspace?.documentId);
    const userId = useSelector((state) => state.auth?.user?.id);
    const [progress, setProgress] = useState(null);
    const [minimized, setMinimized] = useState(false);
    const [finishedSeen, setFinishedSeen] = useState(false);
    const [selectedKey, setSelectedKey] = useState(null);
    const [infoKey, setInfoKey] = useState(null);
    const hiddenPage = HIDDEN_PAGES.some((pattern) => pattern.test(location.pathname));

    useEffect(() => {
        setMinimized(workspaceId ? readPref(userId, prefName(workspaceId, "minimized")) : false);
        setFinishedSeen(workspaceId ? readPref(userId, prefName(workspaceId, "finished")) : false);
        setProgress(null);
        setSelectedKey(null);
    }, [workspaceId, userId]);

    const load = useCallback(async () => {
        if (!workspaceId) return;
        try {
            const {data} = await api.get("/onboarding/progress", {
                headers: {workspace: workspaceId},
                suppressGlobalErrorHandler: true,
            });
            setProgress(data?.data || data);
        } catch {
            // the dock is optional: on errors it simply stays hidden
            setProgress(null);
        }
    }, [workspaceId]);

    // refresh when the page changes, the window regains focus and once a minute
    useEffect(() => {
        load();
    }, [load, location.pathname]);

    useEffect(() => {
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        const timer = window.setInterval(load, REFRESH_MS);
        return () => {
            window.removeEventListener("focus", onFocus);
            window.clearInterval(timer);
        };
    }, [load]);

    useEffect(() => {
        if (!infoKey) return undefined;
        const close = (event) => {
            if (!event.target.closest?.(".onb-step-card")) setInfoKey(null);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [infoKey]);

    const steps = useMemo(() => progress?.steps || [], [progress]);
    const total = steps.length;
    const completed = progress?.completed ?? steps.filter((step) => step.done).length;
    const firstOpen = steps.find((step) => !step.done) || null;
    const projectWithoutGoal = (progress?.hints?.projectsWithoutGoal || 0) > 0;

    // backward guidance: a project without a goal sends the beacon back to the goal step
    const beaconKey = projectWithoutGoal && steps.some((step) => step.key === "goal") ? "goal" : firstOpen?.key;
    const activeKey = selectedKey && steps.some((step) => step.key === selectedKey) ? selectedKey : beaconKey;
    const activeIndex = Math.max(0, steps.findIndex((step) => step.key === activeKey));
    const active = steps[activeIndex];

    if (!workspaceId || !progress?.visible || !total || hiddenPage) return null;
    if (progress.allDone && finishedSeen) return null;

    const text = (key, field) => translate(`onboarding.step.${key}.${field}`);
    const toggleMinimized = (value) => {
        setMinimized(value);
        writePref(userId, prefName(workspaceId, "minimized"), value);
    };
    // closing the completion message: gone for this workspace on every device (the server remembers it);
    // the local flag hides it at once and also covers a failed request
    const finish = () => {
        setFinishedSeen(true);
        writePref(userId, prefName(workspaceId, "finished"), true);
        api.post("/onboarding/dismiss", {}, {headers: {workspace: workspaceId}, suppressGlobalErrorHandler: true}).catch(() => {});
    };
    const goTo = (key) => {
        setInfoKey(null);
        if (STEP_ROUTES[key]) navigate(STEP_ROUTES[key]);
    };

    if (minimized) {
        return (
            <div className={`onb-root ${isDesktop ? "onb-desktop" : "onb-mobile"}`}>
                <button type="button" className="onb-dock-launcher" onClick={() => toggleMinimized(false)}>
                    <span className="onb-launcher-badge">{progress.allDone ? "✓" : total - completed}</span>
                    <span className="onb-launcher-text">{translate("onboarding.launcher")}</span>
                </button>
            </div>
        );
    }

    if (progress.allDone) {
        return (
            <div className={`onb-root ${isDesktop ? "onb-desktop" : "onb-mobile"}`}>
                <div className="onb-zofino-onboarding-dock" role="status">
                    <div className="onb-dock-header" style={{borderBottom: "none", paddingBottom: 0}}>
                        <div className="onb-dock-brand-status">
                            <span className="onb-dock-title-text">{translate("onboarding.all-done.title")}</span>
                            <span className="onb-dock-next-text">{translate("onboarding.all-done.text")}</span>
                        </div>
                        <button type="button" className="onb-popover-action" style={{width: "auto"}} onClick={finish}>
                            {translate("onboarding.all-done.close")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`onb-root ${isDesktop ? "onb-desktop" : "onb-mobile"}`}>
            <div className="onb-zofino-onboarding-dock" role="region" aria-label={translate("onboarding.launcher")}>
                <div className="onb-dock-header">
                    <div className="onb-dock-brand-status">
                        <span className="onb-status-badge">
                            <span className="onb-pulse-dot"/>
                            {translate("onboarding.step-of").replace("{current}", activeIndex + 1).replace("{total}", total)}
                        </span>
                        <span className="onb-dock-title-text">{text(active.key, "hint")}</span>
                        <span className="onb-dock-next-text">• {translate("onboarding.next-action")} {text(active.key, "next")}</span>
                    </div>
                    <div className="onb-dock-header-actions">
                        <span className="onb-progress-counter">
                            {translate("onboarding.completed-count").replace("{done}", completed).replace("{total}", total)}
                        </span>
                        <button type="button" className="onb-dock-dismiss-btn" title={translate("onboarding.minimize")} aria-label={translate("onboarding.minimize")} onClick={() => toggleMinimized(true)}>
                            ✕
                        </button>
                    </div>
                </div>

                {projectWithoutGoal && (
                    <div className="onb-backward-hint">
                        <span>{translate("onboarding.hint.project-without-goal")}</span>
                        <button type="button" onClick={() => {
                            const projectId = progress.hints?.latestProjectWithoutGoal;
                            if (projectId) navigate(`/projects/${projectId}`);
                            else goTo("projects");
                        }}>{translate("onboarding.hint.open")}</button>
                    </div>
                )}

                <div className="onb-steps-row">
                    {steps.map((step, index) => {
                        const isActive = step.key === activeKey;
                        const className = ["onb-step-card", isActive && "onb-active", isActive && projectWithoutGoal && step.key === "goal" && "onb-attention", step.done && "onb-completed", infoKey === step.key && "onb-show-info"]
                            .filter(Boolean).join(" ");
                        return (
                            <div key={step.key} className={className} title={text(step.key, "title")} onClick={() => setSelectedKey(step.key)}>
                                <div className="onb-step-card-main">
                                    <span className="onb-step-number">{step.done ? "✓" : index + 1}</span>
                                    <span className="onb-step-name">{text(step.key, "name")}</span>
                                </div>
                                <span
                                    className="onb-help-icon-btn"
                                    role="button"
                                    tabIndex={0}
                                    title={translate("onboarding.step-help")}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setInfoKey(infoKey === step.key ? null : step.key);
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            setInfoKey(infoKey === step.key ? null : step.key);
                                        }
                                    }}
                                >
                                    ؟
                                </span>
                                <div className="onb-info-popover" onClick={(event) => event.stopPropagation()}>
                                    <span className="onb-popover-tag">
                                        <span className="onb-pulse-dot" style={{width: 5, height: 5}}/>
                                        {text(step.key, "badge")}
                                    </span>
                                    <div className="onb-popover-title">{text(step.key, "question")}</div>
                                    <div className="onb-popover-desc">{text(step.key, "desc")}</div>
                                    {step.target > 1 && (
                                        <div className="onb-popover-desc" style={{marginTop: -6}}>
                                            {translate("onboarding.progress").replace("{count}", Math.min(step.count, step.target)).replace("{target}", step.target)}
                                        </div>
                                    )}
                                    {step.key === "assignees" && !step.done && progress.hints?.unassignedTasks > 0 && (
                                        <div className="onb-popover-desc" style={{marginTop: -6}}>
                                            {translate("onboarding.hint.unassigned-tasks").replace("{count}", progress.hints.unassignedTasks)}
                                        </div>
                                    )}
                                    {step.done ? (
                                        <button type="button" className="onb-popover-action onb-is-done" disabled>
                                            ✓ {translate("onboarding.step-done")}
                                        </button>
                                    ) : (
                                        <button type="button" className="onb-popover-action" onClick={() => goTo(step.key)}>
                                            {text(step.key, "action")}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default OnboardingDock;
